import h from 'react-hyperscript';
import { Route, Switch } from 'react-router-dom';
import snakeCase from 'snake-case';

import AuthenticatedLayout from './layouts/Authenticated';
import Loading from './components/Loading';
import AuthService from './lib/AuthService';

import Aux from './components/Aux/Aux';
import AnalyticsPageView from './components/Analytics/AnalyticsPageView';

import asyncComponent from './components/Async/Async';

const auth = new AuthService();

const handleAuthentication = (nextState, replace) => {
  if (/access_token|id_token|error/.test(nextState.location.hash)) {
    auth.handleAuthentication();
  }
};

const createRoutes = () =>
  h(Aux, [
    h(Switch, [
      h(Route, {
        path: '/signup',
        component: asyncComponent(() => import('./components/SignUp/SignUp')),
      }),
      h(Route, {
        path: '/callback',
        render: props => {
          handleAuthentication(props);

          return h(Loading);
        },
      }),

      h(AuthenticatedLayout, { auth }),
    ]),
    h(Route, { path: '/', component: AnalyticsPageView }), // Always last bc we don't want to track unauthed routes
  ]);

const requiresVenueSelection = true;

// Used to build routing and breadcrumbs
const knownRoutes = [
  // All non-redirect paths are exact = true by default
  { path: '/', name: 'Home', key: 'HOME' },

  {
    path: '/dashboard',
    name: 'Dashboard',
    key: 'DASHBOARD',
    component: asyncComponent(() => import('./pages/Dashboard/Dashboard')),
    requiresVenueSelection,
  },

  {
    path: '/venues',
    name: 'Venues',
    key: 'VENUES',
    component: asyncComponent(() => import('./pages/Venues/Venues')),
  },
  {
    path: '/venues/create',
    name: 'New Venue',
    key: 'VENUES_CREATE',
    component: asyncComponent(() => import('./containers/Venues/VenueCreateContainer')),
  },
  {
    path: '/venues/:id',
    name: 'Venue',
    key: 'VENUES_ID',
    component: asyncComponent(() => import('./pages/Venues/Venue')),
  },

  { path: '/venue', name: 'Venue', key: 'VENUE' },
  {
    path: '/venue/locations/:id/create',
    name: 'Add Location',
    key: 'VENUE_LOCATIONS_ID_CREATE',
    component: asyncComponent(() => import('./pages/VenueLocations/VenueLocation')),
    requiresVenueSelection,
  },
  {
    path: '/venue/locations/:id/edit',
    name: 'Location',
    key: 'VENUE_LOCATIONS_ID_EDIT',
    component: asyncComponent(() => import('./pages/VenueLocations/VenueLocation')),
    requiresVenueSelection,
  },
  {
    path: '/venue/locations/create',
    name: 'New Location',
    key: 'VENUE_LOCATIONS_CREATE',
    component: asyncComponent(() => import('./pages/VenueLocations/VenueLocation')),
    requiresVenueSelection,
  },
  {
    path: '/venue/locations/:id',
    name: 'Location',
    key: 'VENUE_LOCATIONS_ID',
    component: asyncComponent(() => import('./pages/VenueLocations/VenueLocations')),
    requiresVenueSelection,
  },
  {
    path: '/venue/locations',
    name: 'Locations',
    key: 'VENUE_LOCATIONS',
    component: asyncComponent(() => import('./pages/VenueLocations/VenueLocations')),
    requiresVenueSelection,
  },
  {
    path: '/venue/settings',
    name: 'Settings',
    key: 'VENUE_SETTINGS',
    requiresVenueSelection,
  },

  {
    path: '/events',
    name: 'Events',
    key: 'EVENTS',
    component: asyncComponent(() => import('./pages/Events/Events')),
    requiresVenueSelection,
  },
  {
    path: '/events/create',
    name: 'New Event',
    key: 'EVENTS_CREATE',
    component: asyncComponent(() => import('./containers/Events/EventCreateContainer')),
    requiresVenueSelection,
  },
  {
    path: '/events/:id/edit',
    name: 'Edit',
    key: 'EVENTS_ID',
    component: asyncComponent(() => import('./pages/Events/Event')),
    requiresVenueSelection,
  },
  {
    path: '/events/:id/copy',
    name: 'Event',
    key: 'EVENTS_ID_DUPLICATE',
    component: asyncComponent(() => import('./pages/Events/EventDuplicate')),
    requiresVenueSelection,
  },
  {
    path: '/events/:id',
    name: 'Event',
    key: 'EVENTS_CTA',
    component: asyncComponent(() => import('./pages/Events/EventLanding')),
    requiresVenueSelection,
  },
  {
    path: '/events/:id/workers',
    name: 'Workers',
    key: 'EVENTS_ID_VOLUNTEERS',
    component: asyncComponent(() => import('./pages/Events/Volunteers/Volunteers')),
    requiresVenueSelection,
  },
  {
    path: '/events/:id/organizations',
    name: 'Organizations',
    key: 'EVENTS_ID_NPOS',
    component: asyncComponent(() => import('./pages/Events/Npos/EventNposAssign')),
    requiresVenueSelection,
  },
  {
    path: '/events/:eventId/organizations/:npoId',
    name: 'Organizations',
    key: 'EVENTS_ID_NPOS_ID',
    component: asyncComponent(() => import('./pages/Events/Npos/EventNpo')),
    requiresVenueSelection,
  },
  {
    path: '/events/:id/locations',
    name: 'Locations',
    key: 'EVENTS_ID_LOCATIONS',
    component: asyncComponent(() => import('./pages/Events/Locations/EventLocationsLanding')),
    requiresVenueSelection,
  },
  {
    path: '/events/:eventId/locations/:eventVenueLocationId',
    name: 'Location',
    key: 'EVENTS_ID_LOCATIONS_ID',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Events/Venue/EventVenueLocation')),
  },

  {
    path: '/organizations',
    name: 'Organizations',
    key: 'ORGANIZATIONS',
    component: asyncComponent(() => import('./pages/Npos/Npos')),
    requiresVenueSelection,
  },
  {
    path: '/organizations/create',
    name: 'New Organization',
    key: 'ORGANIZATION_CREATE',
    component: asyncComponent(() => import('./containers/Npos/NpoCreateContainer')),
    requiresVenueSelection,
  },
  {
    path: '/organizations/:id',
    name: 'Organization',
    key: 'ORGANIZATIONS_ID',
    component: asyncComponent(() => import('./pages/Npos/Npo')),
    requiresVenueSelection,
  },
  { path: '/organization', name: 'Settings', key: 'ORGANIZATION', requiresVenueSelection },

  {
    path: '/workers',
    name: 'Workers',
    key: 'VOLUNTEERS',
    component: asyncComponent(() => import('./pages/Volunteers/Volunteers')),
    requiresVenueSelection,
  },
  {
    path: '/workers/checkin',
    name: 'Check-In',
    key: 'VOLUNTEERS_CHECKIN',
    requiresVenueSelection,
    component: asyncComponent(() => import('./containers/Volunteers/VolunteerCheckinContainer')),
  },
  {
    path: '/workers/checkin2',
    name: 'Check-In',
    key: 'VOLUNTEERS_CHECKIN2',
    requiresVenueSelection,
    component: asyncComponent(() => import('./containers/Volunteers/VolunteerCheckinContainer2')),
  },
  {
    path: '/workers/create',
    name: 'Add Worker',
    key: 'VOLUNTEERS_CREATE',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Volunteers/VolunteerCreate')),
  },
  {
    path: '/workers/:id',
    name: 'Worker',
    key: 'VOLUNTEERS_ID',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Volunteers/Volunteer')),
  },

  {
    path: '/reporting',
    name: 'Reporting',
    key: 'REPORTS',
    requiresVenueSelection,
  },
  {
    path: '/reporting/venue',
    name: 'Venue',
    key: 'REPORTS_VENUE',
    requiresVenueSelection,
    // component: asyncComponent(() => import('./pages/Reports/ReportsDashboard')),
  },
  {
    path: '/reporting/events',
    name: 'Events',
    key: 'REPORTS_EVENTS',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Reports/Events/EventSelection')),
  },
  {
    path: '/reporting/events/:id',
    name: 'Event',
    key: 'REPORTS_EVENTS_ID',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Reports/Events/EventReportsLanding')),
  },
  {
    path: '/reporting/events/:id/locations-at-risk',
    name: 'At Risk Locations',
    requiresVenueSelection,
    key: 'REPORTS_EVENTS_AT_RISK',
    component: asyncComponent(() => import('./pages/Reports/Events/LocationsAtRiskReport')),
  },
  {
    path: '/reporting/events/:id/fulfillment',
    name: 'Fulfillment',
    requiresVenueSelection,
    key: 'REPORTS_EVENTS_FULFILLMENT',
  },
  {
    path: '/reporting/events/:id/uniforms',
    name: 'Uniforms',
    requiresVenueSelection,
    key: 'REPORTS_EVENTS_UNIFORMS',
    component: asyncComponent(() => import('./pages/Reports/Events/EventUniformReport')),
  },
  {
    path: '/reporting/events/:id/roles',
    name: 'Event Roles',
    requiresVenueSelection,
    key: 'REPORTS_EVENTS_ROLES',
    component: asyncComponent(() => import('./pages/Reports/Events/EventFulfillmentByRoleReport')),
  },
  {
    path: '/reporting/events/:id/fulfillment/:evlId',
    name: 'Location',
    key: 'REPORTS_EVENTS_FULFILLMENT_ID',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Reports/Events/FulfillmentByLocationReports')),
  },
  {
    path: '/reporting/events/:id/open-close',
    name: 'Open & Close Report',
    key: 'REPORTS_EVENTS_ID_OPEN_CLOSE',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Reports/Events/EventOpenCloseReportLanding')),
  },
  {
    path: '/reporting/events/:id/roles',
    name: 'Event Location Roles Report',
    key: 'REPORTS_EVENTS_ID_ROLES',
    requiresVenueSelection,
    component: asyncComponent(() => import('./pages/Reports/Events/EventFulfillmentByRoleReport')),
  },
  {
    path: '/reporting/organizations',
    name: 'Organizations',
    key: 'REPORTS_ORGANIZATIONS',
    requiresVenueSelection,
    // component: asyncComponent(() => import('./pages/Reports/ReportsDashboard')),
  },

  {
    path: '/users',
    name: 'Users',
    key: 'USERS',
    component: asyncComponent(() => import('./containers/Users/UsersContainer')),
    requiresVenueSelection,
  },

  { path: '/profile', name: 'Profile' },
  {
    path: '/profile/settings',
    name: 'Settings',
    key: 'USER_SETTINGS',
    component: asyncComponent(() => import('./pages/Settings/Settings')),
    requiresVenueSelection,
  },
  { path: '/profile/subscription', name: 'Subscription', key: 'USER_SETTINGS_SUBSCRIPTION' },

  {
    path: '/me',
    name: 'Me',
    key: 'USER_DEBUG',
    component: asyncComponent(() => import('./pages/Me/Me')),
    requiresVenueSelection,
  },

  { path: '/legal', name: 'Legal' },
  {
    path: '/legal/terms-of-service',
    name: 'Terms of Service',
    key: 'TERMS_OF_SERVICE_APPROVAL',
    component: asyncComponent(() => import('./pages/TermsOfService/TermsOfServiceApproval')),
    requiresVenueSelection,
  },

  { path: '/', redirect: '/dashboard' },
  { path: '/profile', redirect: '/profile/settings' },
  { path: '/venue', redirect: '/venue/locations' },
  { path: '/legal', redirect: '/legal/terms-of-service' },
  { path: '/reporting', redirect: '/reporting/events' },
  { path: '/reporting/events/:id/fulfillment', redirectWithParams: '/reporting/events/:id' },

  { path: '/404', name: 'Not Found', component: asyncComponent(() => import('./pages/NotFound/NotFound')) },
].map(rte => ({ ...rte, path: rte.path.toLowerCase() }));

const lookupRouteByKey = key => {
  const found = knownRoutes.find(rte => (rte.key || '').toUpperCase() === (key || '').toUpperCase());

  if (!found) {
    const defaultRoute = knownRoutes.find(rte => (rte.key || '').toUpperCase() === 'HOME');

    return defaultRoute;
  }

  return found;
};

const routeMap = knownRoutes.filter(rte => rte.key).reduce(
  (acc, rte) => ({
    ...acc,
    [snakeCase(rte.key).toUpperCase()]: rte,
  }),
  {},
);

const analyticsBlackList = [routeMap.TERMS_OF_SERVICE_APPROVAL.path];

export { knownRoutes, lookupRouteByKey, routeMap, analyticsBlackList };

export default createRoutes;



// WEBPACK FOOTER //
// ./src/routes.js